/**
 * Gerenciador de armazenamento para dados de autenticação
 * Implementa cache seguro com fallback para diferentes storages
 */

interface AuthTokens {
  access: string;
  refresh: string;
  expiresAt?: number;
}

import { AuthUser } from '../../types/user';

interface CachedAuthData {
  tokens: AuthTokens;
  user: AuthUser;
  cachedAt: number;
  lastActivity: number;
}

class AuthStorageManager {
  private readonly ACCESS_TOKEN_KEY = 'AUTH_ACCESS';
  private readonly REFRESH_TOKEN_KEY = 'AUTH_REFRESH';
  private readonly USER_DATA_KEY = 'AUTH_USER_DATA';
  private readonly CACHED_AUTH_KEY = 'DESIRIUS_AUTH_CACHE';
  private readonly ACTIVITY_KEY = 'AUTH_LAST_ACTIVITY';
  
  // Cache em memória para melhor performance
  private memoryCache: CachedAuthData | null = null;
  private cacheTimeout = 5 * 60 * 1000; // 5 minutos
  private activityTimeout = 8 * 60 * 60 * 1000; // 8 horas (igual ao backend)

  /**
   * Salva dados de autenticação com múltiplos storages
   */
  saveAuthData(tokens: AuthTokens, user: AuthUser): void {
    const now = Date.now();
    
    const authData: CachedAuthData = {
      tokens,
      user,
      cachedAt: now,
      lastActivity: now
    };

    try {
      // Salvar no localStorage (principal)
      localStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.access);
      localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refresh);
      localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(user));
      localStorage.setItem(this.CACHED_AUTH_KEY, JSON.stringify(authData));
      localStorage.setItem(this.ACTIVITY_KEY, now.toString());

      // Backup no sessionStorage
      sessionStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.access);
      sessionStorage.setItem(this.USER_DATA_KEY, JSON.stringify(user));

      // Cache em memória
      this.memoryCache = authData;
      
      console.log('✅ Dados de autenticação salvos com sucesso');
    } catch (error) {
      console.error('❌ Erro ao salvar dados de autenticação:', error);
    }
  }

  /**
   * Recupera dados de autenticação com fallback
   */
  getAuthData(): CachedAuthData | null {
    // 1. Verificar cache em memória primeiro
    if (this.memoryCache && this.isMemoryCacheValid()) {
      this.updateActivity();
      return this.memoryCache;
    }

    try {
      // 2. Tentar localStorage
      const cachedData = localStorage.getItem(this.CACHED_AUTH_KEY);
      if (cachedData) {
        const data: CachedAuthData = JSON.parse(cachedData);
        
        if (this.isAuthDataValid(data)) {
          this.memoryCache = data;
          this.updateActivity();
          return data;
        }
      }

      // 3. Fallback para dados separados no localStorage
      const access = localStorage.getItem(this.ACCESS_TOKEN_KEY);
      const refresh = localStorage.getItem(this.REFRESH_TOKEN_KEY);
      const userData = localStorage.getItem(this.USER_DATA_KEY);

      if (access && userData) {
        const user: AuthUser = JSON.parse(userData);
        const data: CachedAuthData = {
          tokens: { access, refresh: refresh || '' },
          user,
          cachedAt: Date.now(),
          lastActivity: Date.now()
        };
        
        this.memoryCache = data;
        this.updateActivity();
        return data;
      }

      // 4. Último fallback: sessionStorage
      const sessionAccess = sessionStorage.getItem(this.ACCESS_TOKEN_KEY);
      const sessionUser = sessionStorage.getItem(this.USER_DATA_KEY);

      if (sessionAccess && sessionUser) {
        const user: AuthUser = JSON.parse(sessionUser);
        const data: CachedAuthData = {
          tokens: { access: sessionAccess, refresh: '' },
          user,
          cachedAt: Date.now(),
          lastActivity: Date.now()
        };
        
        console.log('⚠️ Usando dados do sessionStorage como fallback');
        return data;
      }

    } catch (error) {
      console.error('❌ Erro ao recuperar dados de autenticação:', error);
      this.clearAuthData(); // Limpar dados corrompidos
    }

    return null;
  }

  /**
   * Recupera apenas o access token (compatibilidade)
   */
  getAccessToken(): string {
    // Cache em memória primeiro
    if (this.memoryCache && this.isMemoryCacheValid()) {
      return this.memoryCache.tokens.access;
    }

    // Fallback para localStorage
    return localStorage.getItem(this.ACCESS_TOKEN_KEY) || 
           sessionStorage.getItem(this.ACCESS_TOKEN_KEY) || '';
  }

  /**
   * Recupera apenas o refresh token
   */
  getRefreshToken(): string {
    if (this.memoryCache && this.isMemoryCacheValid()) {
      return this.memoryCache.tokens.refresh;
    }

    return localStorage.getItem(this.REFRESH_TOKEN_KEY) || '';
  }

  /**
   * Atualiza apenas os tokens (refresh automático)
   */
  updateTokens(tokens: AuthTokens): void {
    const authData = this.getAuthData();
    if (authData) {
      authData.tokens = tokens;
      authData.cachedAt = Date.now();
      authData.lastActivity = Date.now();
      
      this.saveAuthData(tokens, authData.user);
    }
  }

  /**
   * Atualiza timestamp de atividade
   */
  updateActivity(): void {
    const now = Date.now();
    localStorage.setItem(this.ACTIVITY_KEY, now.toString());
    
    if (this.memoryCache) {
      this.memoryCache.lastActivity = now;
    }
  }

  /**
   * Verifica se o usuário está logado
   */
  isLoggedIn(): boolean {
    const authData = this.getAuthData();
    return authData !== null && this.isSessionActive();
  }

  /**
   * Verifica se a sessão ainda está ativa
   */
  isSessionActive(): boolean {
    const lastActivity = localStorage.getItem(this.ACTIVITY_KEY);
    if (!lastActivity) return false;

    const timeSinceActivity = Date.now() - parseInt(lastActivity);
    return timeSinceActivity < this.activityTimeout;
  }

  /**
   * Limpa todos os dados de autenticação
   */
  clearAuthData(): void {
    // Limpar localStorage
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_DATA_KEY);
    localStorage.removeItem(this.CACHED_AUTH_KEY);
    localStorage.removeItem(this.ACTIVITY_KEY);

    // Limpar sessionStorage
    sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(this.USER_DATA_KEY);

    // Limpar cache em memória
    this.memoryCache = null;

    console.log('🧹 Dados de autenticação limpos');
  }

  /**
   * Verifica se o cache em memória ainda é válido
   */
  private isMemoryCacheValid(): boolean {
    if (!this.memoryCache) return false;
    
    const age = Date.now() - this.memoryCache.cachedAt;
    const isValid = age < this.cacheTimeout && this.isSessionActive();
    
    if (!isValid) {
      this.memoryCache = null;
    }
    
    return isValid;
  }

  /**
   * Verifica se os dados de autenticação ainda são válidos
   */
  private isAuthDataValid(data: CachedAuthData): boolean {
    const now = Date.now();
    
    // Verificar se não é muito antigo
    if (now - data.cachedAt > this.cacheTimeout) {
      return false;
    }

    // Verificar se a sessão ainda está ativa
    if (now - data.lastActivity > this.activityTimeout) {
      return false;
    }

    // Verificar se tem dados essenciais
    return !!(data.tokens.access && data.user.id);
  }

  /**
   * Obtém estatísticas do cache para debugging
   */
  getCacheStats(): object {
    return {
      hasMemoryCache: !!this.memoryCache,
      hasLocalStorage: !!localStorage.getItem(this.ACCESS_TOKEN_KEY),
      hasSessionStorage: !!sessionStorage.getItem(this.ACCESS_TOKEN_KEY),
      isSessionActive: this.isSessionActive(),
      lastActivity: localStorage.getItem(this.ACTIVITY_KEY),
      cacheAge: this.memoryCache ? Date.now() - this.memoryCache.cachedAt : null
    };
  }
}

// Singleton instance
export const authStorage = new AuthStorageManager();

// Export types
export type { AuthTokens, AuthUser, CachedAuthData };